import { useEffect, useMemo, useState } from "react";
import { Bell, BellOff, Send, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/Kpi";

const b64ToU8 = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = window.atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};
const isIphone = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
const supportato = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/** Iscrizione push del dispositivo + preferenze (eventi e convenzionati) dell'utente. */
export function NotifichePush() {
  const [sub, setSub] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [cerca, setCerca] = useState("");
  const [busy, setBusy] = useState(false);

  const carica = () => api.get("/push/preferenze").then((r) => setPrefs(r.data)).catch(() => {});
  useEffect(() => {
    carica();
    api.get("/dashboard/dealers-periodo?anno=2026").then((r) => setDealers(r.data.dealers || [])).catch(() => {});
    if (supportato()) {
      navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then(setSub).catch(() => {});
    }
  }, []);

  const attiva = async () => {
    if (!supportato()) {
      toast.error(isIphone() && !standalone()
        ? "Su iPhone: Condividi → Aggiungi a schermata Home, poi apri l'app dall'icona e riprova"
        : "Questo browser non supporta le notifiche push");
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Permesso notifiche non concesso"); return; }
      const { data } = await api.get("/push/vapid-public");
      const reg = await navigator.serviceWorker.ready;
      const s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.publicKey) });
      await api.post("/push/subscribe", { ...s.toJSON(), dispositivo: navigator.userAgent.slice(0, 120) });
      setSub(s);
      await carica();
      toast.success("Notifiche attivate su questo dispositivo");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const disattiva = async () => {
    if (!sub) return;
    setBusy(true);
    try {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await api.delete("/push/subscribe", { data: { endpoint } });
      setSub(null);
      await carica();
      toast.success("Notifiche disattivate su questo dispositivo");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const salva = async (next) => {
    setPrefs(next);
    try {
      await api.put("/push/preferenze", {
        eventi: Object.fromEntries(next.eventi.map((e) => [e.chiave, e.attivo])),
        dealer_codici: next.dealer_codici,
      });
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleEvento = (k) => salva({ ...prefs, eventi: prefs.eventi.map((e) => (e.chiave === k ? { ...e, attivo: !e.attivo } : e)) });
  const toggleDealer = (c) => salva({ ...prefs, dealer_codici: prefs.dealer_codici.includes(c)
    ? prefs.dealer_codici.filter((x) => x !== c) : [...prefs.dealer_codici, c] });

  const testa = async () => {
    try { await api.post("/push/test"); toast.success("Notifica di prova inviata"); } catch (e) { toast.error(apiError(e)); }
  };

  const convAttivo = prefs?.eventi.some((e) => e.chiave === "convenzionati" && e.attivo);
  const filtrati = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    const sel = new Set(prefs?.dealer_codici || []);
    return dealers.filter((d) => sel.has(d.codice) || !q || (d.ragione_sociale || "").toLowerCase().includes(q) || d.codice.includes(q))
      .sort((a, b) => Number(sel.has(b.codice)) - Number(sel.has(a.codice))).slice(0, q ? 40 : 15);
  }, [dealers, cerca, prefs]);

  return (
    <Card className="p-6 lg:col-span-2" data-testid="account-notifiche">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">Notifiche push</div>
          <div className="text-sm text-slate-600 mt-1">
            Ricevi le notifiche sul telefono o sul PC anche con l'app chiusa.
            {prefs?.dispositivi > 0 && <span className="ml-1 text-slate-400">· {prefs.dispositivi} dispositivo/i iscritti</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {sub ? (
            <>
              <button type="button" onClick={testa} data-testid="push-test"
                className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-slate-300 bg-white
                  text-slate-700 hover:border-sky-400 transition-colors duration-200"><Send size={14} /> Prova</button>
              <button type="button" onClick={disattiva} disabled={busy} data-testid="push-disattiva"
                className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-slate-300 bg-white
                  text-slate-600 hover:border-red-400 hover:text-red-600 disabled:opacity-50 transition-colors duration-200">
                <BellOff size={14} /> Disattiva su questo dispositivo</button>
            </>
          ) : (
            <button type="button" onClick={attiva} disabled={busy} data-testid="push-attiva"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-sky-600 text-white font-semibold
                hover:bg-sky-700 disabled:opacity-50 transition-colors duration-200">
              <Bell size={14} /> Attiva notifiche su questo dispositivo</button>
          )}
        </div>
      </div>

      {isIphone() && !standalone() && (
        <div className="mb-4 text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 flex gap-2"
          data-testid="push-avviso-iphone">
          <Smartphone size={14} className="shrink-0 mt-0.5" />
          Su iPhone le notifiche funzionano solo dall'app installata: Condividi → «Aggiungi a schermata Home», poi apri DBE dall'icona.
        </div>
      )}

      {prefs && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-2">Cosa vuoi ricevere</div>
            <div className="space-y-2">
              {prefs.eventi.map((e) => (
                <label key={e.chiave} className="flex items-start gap-3 text-sm cursor-pointer" data-testid={`push-evento-${e.chiave}`}>
                  <input type="checkbox" checked={e.attivo} onChange={() => toggleEvento(e.chiave)}
                    className="mt-1 accent-sky-600" data-testid={`push-evento-${e.chiave}-check`} />
                  <span>
                    <span className="font-medium text-slate-800">{e.label}</span>
                    {e.chiave === "riepilogo" && <span className="text-slate-400"> · alle {prefs.ora_riepilogo}</span>}
                    <div className="text-xs text-slate-500">{e.descrizione}</div>
                  </span>
                </label>
              ))}
              {!prefs.eventi.length && <div className="text-sm text-slate-400">Nessuna notifica abilitata dal MASTER.</div>}
            </div>
          </div>
          {convAttivo && (
            <div data-testid="push-dealer-box">
              <div className="text-xs font-semibold text-slate-500 mb-2">
                Convenzionati da seguire <span className="text-slate-400">· {prefs.dealer_codici.length} selezionati</span>
              </div>
              <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca convenzionato…"
                data-testid="push-dealer-cerca"
                className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-sky-300" />
              <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                {filtrati.map((d) => (
                  <label key={d.codice} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`push-dealer-${d.codice}`}>
                    <input type="checkbox" checked={prefs.dealer_codici.includes(d.codice)} onChange={() => toggleDealer(d.codice)}
                      className="accent-sky-600" />
                    <span className="truncate">{d.ragione_sociale}</span>
                    <span className="ml-auto text-xs text-slate-400 font-mono">{d.pratiche_caricate}</span>
                  </label>
                ))}
                {!filtrati.length && <div className="text-xs text-slate-400">Nessun convenzionato trovato</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
